/** @forge-header
 * @id          consolidatedMoneylines
 * @role        Consolidated moneyline table | single-row per game, dual-venue odds side-by-side
 * @pattern     component
 * @tier        T2
 * @purity      none
 * @units       cents
 * @money-path  yes
 * @fan-out     ~1
 * @upstream    nbaConsolidated/types:ConsolidatedGameBooks
 *              | sportsDiscovery:getSeriesTicker,Sport
 *              | hooks/useTableSort:useTableSort
 *              | sportsStream:SportsLoadingState
 *              | ./MoneylineGameRow:MoneylineGameRow
 *              | ./tradeTypes:TradeClickRequest
 * @downstream  ValueDashboardView
 * @exports     ConsolidatedMoneylines:component | TradeClickRequest:type (re-export)
 * @test        untested
 * @state       showAllRows:boolean | sort via useTableSort
 */

import { cn } from '@/lib/utils';
import { Th, Td } from '@/components/atoms/TableCells';
import { SortableTh } from '@/components/atoms/SortableTh';
import { useMemo, useState } from 'react';
import { useTableSort } from '@/hooks/useTableSort';
import type { ConsolidatedGameBooks } from '@/lib/nbaConsolidated/types';
import { getSeriesTicker, type Sport } from '@/lib/sportsDiscovery';
import { ChevronDown, ChevronUp, ChevronsUpDown, Minus } from 'lucide-react';
import type { OddsMode } from './DashboardHeader';
import type { SportsLoadingState } from '@/lib/sportsStream';
import { LoadingPhaseStatus } from './LoadingPhaseStatus';
import { MoneylineGameRow } from './MoneylineGameRow';

// Re-export TradeClickRequest for backwards compatibility
export type { TradeClickRequest } from './tradeTypes';
import type { TradeClickRequest } from './tradeTypes';

interface ConsolidatedMoneylinesProps {
  games: ConsolidatedGameBooks[];
  isLoading?: boolean;
  loadingState?: SportsLoadingState;
  oddsMode: OddsMode;
  maxLiquidity?: number;
  expandedGameKeys: Set<string>;
  onToggleExpand: (gameKey: string) => void;
  onTradeClick?: (req: TradeClickRequest) => void;
  /** Navigate to MarketDetailView (primary click). Shift+click still opens trade modal. */
  onNavigateToMarket?: (marketTicker: string) => void;
  /** Sport for team name lookup and title (default: nba) */
  sport?: Sport;
  /** Collapse the entire table body, showing only the header bar */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** Compact mode — reduces header padding */
  compact?: boolean;
  onToggleCompact?: () => void;
  className?: string;
}

export function ConsolidatedMoneylines({
  games,
  isLoading = false,
  loadingState,
  maxLiquidity = 100000,
  oddsMode,
  expandedGameKeys,
  onToggleExpand,
  onTradeClick,
  onNavigateToMarket,
  sport = 'nba',
  collapsed = false,
  onToggleCollapsed,
  compact = false,
  onToggleCompact,
  className,
}: ConsolidatedMoneylinesProps) {
  const [showAllRows, setShowAllRows] = useState(true);
  const maxRows = 10;

  const sortExtractors = useMemo(
    () => ({
      rotation: (game: ConsolidatedGameBooks) => game.awayRotation ?? null,
    }),
    []
  );

  const { sorted, sortKey, sortDir, handleSort } = useTableSort(games, sortExtractors, {
    defaultSortKey: 'time',
    defaultSortDir: 'asc',
  });

  const visibleGames = useMemo(() => {
    if (showAllRows) return sorted;
    return sorted.slice(0, maxRows);
  }, [sorted, showAllRows]);

  const sportLabel = sport.toUpperCase().replace('-', ' ');
  const seriesTicker = getSeriesTicker(sport, 'moneyline');

  return (
    <div className={cn('bg-card border-border rounded-lg border', className)}>
      {/* Header with sport-prefixed title and series ticker */}
      <div
        className={cn('border-border border-b px-4 transition-all', compact ? 'py-1.5' : 'py-4')}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2
              className={cn(
                'text-foreground font-semibold transition-all',
                compact ? 'text-sm' : 'text-lg',
                onToggleCollapsed && 'cursor-pointer'
              )}
              onClick={onToggleCollapsed}
            >
              {sportLabel} Moneylines
            </h2>
            {collapsed && (
              <span className="text-muted-foreground text-xs">
                {games.length} game{games.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {onToggleCompact && !collapsed && (
              <button
                onClick={onToggleCompact}
                className={cn(
                  'text-muted-foreground hover:text-foreground rounded p-0.5 transition-colors',
                  compact && 'text-foreground'
                )}
                title={compact ? 'Comfortable spacing' : 'Compact spacing'}
              >
                {compact ? (
                  <ChevronsUpDown className="h-3.5 w-3.5" />
                ) : (
                  <Minus className="h-3.5 w-3.5" />
                )}
              </button>
            )}
            {seriesTicker && (
              <span className="text-muted-foreground font-mono text-xs">{seriesTicker}</span>
            )}
            <LoadingPhaseStatus
              isLoading={isLoading}
              loadingState={loadingState}
            />
            {onToggleCollapsed && (
              <button
                onClick={onToggleCollapsed}
                className="text-muted-foreground hover:text-foreground rounded p-0.5 transition-colors"
                title={collapsed ? 'Expand section' : 'Collapse section'}
              >
                {collapsed ? <ChevronDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table + footer — hidden when collapsed */}
      {!collapsed && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-border border-b">
                <tr>
                  <SortableTh
                    sortKey="rotation"
                    activeSortKey={sortKey}
                    activeSortDir={sortDir}
                    onSort={handleSort}
                    className="w-16"
                  >
                    Roto
                  </SortableTh>
                  <SortableTh
                    sortKey="date"
                    activeSortKey={sortKey}
                    activeSortDir={sortDir}
                    onSort={handleSort}
                  >
                    Game Date
                  </SortableTh>
                  <SortableTh
                    sortKey="time"
                    activeSortKey={sortKey}
                    activeSortDir={sortDir}
                    onSort={handleSort}
                  >
                    Time PT
                  </SortableTh>
                  <SortableTh
                    sortKey="team"
                    activeSortKey={sortKey}
                    activeSortDir={sortDir}
                    onSort={handleSort}
                  >
                    Away Team
                  </SortableTh>
                  <Th>Home Team</Th>
                  <Th className="text-right">Away Kalshi</Th>
                  <Th className="text-right">Home Kalshi</Th>
                  <Th className="w-16 text-right">Away K Vol</Th>
                  <Th className="w-16 text-right">Home K Vol</Th>
                  <Th className="w-24">Away K Liq</Th>
                  <Th className="w-24">Home K Liq</Th>
                  <Th className="text-right">Away Poly</Th>
                  <Th className="text-right">Home Poly</Th>
                  <Th className="w-16 text-right">Away P Vol</Th>
                  <Th className="w-16 text-right">Home P Vol</Th>
                  <Th className="w-24">Away P Liq</Th>
                  <Th className="w-24">Home P Liq</Th>
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {isLoading && games.length === 0
                  ? Array.from({ length: 10 }).map((_, i) => (
                      <tr
                        key={`loading-${i}`}
                        className="animate-pulse"
                      >
                        <Td>
                          <div className="bg-muted/40 h-3 w-12 rounded" />
                        </Td>
                        <Td>
                          <div className="bg-muted/40 h-3 w-20 rounded" />
                        </Td>
                        <Td>
                          <div className="bg-muted/40 h-3 w-14 rounded" />
                        </Td>
                        <Td>
                          <div className="bg-muted/40 h-3 w-24 rounded" />
                        </Td>
                        <Td>
                          <div className="bg-muted/40 h-3 w-24 rounded" />
                        </Td>
                        <Td className="text-right">
                          <div className="bg-muted/40 ml-auto h-3 w-10 rounded" />
                        </Td>
                        <Td className="text-right">
                          <div className="bg-muted/40 ml-auto h-3 w-10 rounded" />
                        </Td>
                        <Td className="text-right">
                          <div className="bg-muted/40 ml-auto h-3 w-8 rounded" />
                        </Td>
                        <Td className="text-right">
                          <div className="bg-muted/40 ml-auto h-3 w-8 rounded" />
                        </Td>
                        <Td className="p-1">
                          <div className="bg-muted/30 h-6 w-full rounded" />
                        </Td>
                        <Td className="p-1">
                          <div className="bg-muted/30 h-6 w-full rounded" />
                        </Td>
                        <Td className="text-right">
                          <div className="bg-muted/40 ml-auto h-3 w-10 rounded" />
                        </Td>
                        <Td className="text-right">
                          <div className="bg-muted/40 ml-auto h-3 w-10 rounded" />
                        </Td>
                        <Td className="text-right">
                          <div className="bg-muted/40 ml-auto h-3 w-8 rounded" />
                        </Td>
                        <Td className="text-right">
                          <div className="bg-muted/40 ml-auto h-3 w-8 rounded" />
                        </Td>
                        <Td className="p-1">
                          <div className="bg-muted/30 h-6 w-full rounded" />
                        </Td>
                        <Td className="p-1">
                          <div className="bg-muted/30 h-6 w-full rounded" />
                        </Td>
                      </tr>
                    ))
                  : visibleGames.map((game) => (
                      <MoneylineGameRow
                        key={game.key}
                        game={game}
                        oddsMode={oddsMode}
                        maxLiquidity={maxLiquidity}
                        expanded={expandedGameKeys.has(game.key)}
                        sport={sport}
                        onToggleExpand={onToggleExpand}
                        onTradeClick={onTradeClick}
                        onNavigateToMarket={onNavigateToMarket}
                      />
                    ))}
              </tbody>
            </table>
          </div>

          {/* Expand / minimize (show more rows) */}
          {games.length > maxRows && (
            <div className="border-border/50 flex items-center justify-center border-t p-2">
              <button
                type="button"
                onClick={() => setShowAllRows((v) => !v)}
                className="text-muted-foreground hover:text-foreground hover:bg-muted/30 flex items-center gap-2 rounded px-3 py-1.5 text-xs transition-colors"
                aria-expanded={showAllRows}
                aria-label={showAllRows ? 'Show fewer games' : 'Show more games'}
              >
                <span className="tabular-nums">
                  {showAllRows
                    ? `Showing ${games.length}`
                    : `Showing ${Math.min(maxRows, games.length)} of ${games.length}`}
                </span>
                {showAllRows ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>
            </div>
          )}

          {/* Empty state */}
          {games.length === 0 && !isLoading && (
            <div className="text-muted-foreground p-8 text-center">No games available</div>
          )}
        </>
      )}
    </div>
  );
}

export default ConsolidatedMoneylines;
